import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { favouritesAPI, applicationsAPI } from '../utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Heart, MapPin, Briefcase, Trash2, Loader2, Search, Calendar, Star,
  CheckCircle2, AlertTriangle, ArrowRight, Send, FileText, User, Banknote
} from 'lucide-react';

const STATUS_STYLE = {
  active:    { stripe: 'bg-emerald-500', pill: 'text-emerald-800 bg-emerald-50 border-emerald-200' },
  expired:   { stripe: 'bg-slate-300',   pill: 'text-slate-700 bg-slate-50 border-slate-200' },
  filled:    { stripe: 'bg-blue-400',    pill: 'text-blue-800 bg-blue-50 border-blue-200' },
  cancelled: { stripe: 'bg-slate-300',   pill: 'text-slate-700 bg-slate-50 border-slate-200' },
  paused:    { stripe: 'bg-amber-400',   pill: 'text-amber-800 bg-amber-50 border-amber-200' },
  draft:     { stripe: 'bg-slate-300',   pill: 'text-slate-700 bg-slate-50 border-slate-200' }
};

export default function Favourites() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [appliedIds, setAppliedIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { reload(); }, []);

  const reload = async () => {
    setLoading(true);
    try {
      const [favs, apps] = await Promise.all([
        favouritesAPI.list(),
        applicationsAPI.getMy().catch(() => ({ data: [] }))
      ]);
      setJobs(favs.data || []);
      setAppliedIds(new Set((apps.data || []).map(a => a.job_id)));
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  };

  const remove = async (jobId) => {
    await favouritesAPI.remove(jobId);
    setJobs((js) => js.filter((j) => j.id !== jobId));
  };

  const formatSalary = (j) => {
    const locale = i18n.language === 'en' ? 'en-US' : i18n.language === 'fr' ? 'fr-FR' : 'pt-PT';
    if (j.salary_range_min && j.salary_range_max)
      return `${Number(j.salary_range_min).toLocaleString(locale)} – ${Number(j.salary_range_max).toLocaleString(locale)} ${j.currency || 'CVE'}`;
    if (j.salary_range_min)
      return t('favourites.salaryFrom', { value: Number(j.salary_range_min).toLocaleString(locale), currency: j.currency || 'CVE' });
    return null;
  };

  const counts = useMemo(() => {
    const c = { all: jobs.length, active: 0, inactive: 0 };
    jobs.forEach(j => {
      if (j.status === 'active') c.active++;
      else c.inactive++;
    });
    return c;
  }, [jobs]);

  const filtered = useMemo(() => {
    if (filter === 'all') return jobs;
    if (filter === 'active') return jobs.filter(j => j.status === 'active');
    return jobs.filter(j => j.status !== 'active');
  }, [jobs, filter]);

  return (
    <>
      <Helmet>
        <title>{t('favourites.seoTitle')}</title>
        <meta name="description" content={t('favourites.seoDescription')} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://trabadju.cv/favoritos" />
      </Helmet>

      <main className="min-h-screen bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
          <header className="mb-6">
            <p className="text-[11px] font-bold uppercase tracking-widest text-rose-600 mb-1.5">{t('favourites.eyebrow')}</p>
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5" data-testid="favourites-title">
                  <Heart className="h-7 w-7 fill-rose-500 text-rose-500 flex-none" aria-hidden="true" />
                  {t('favourites.title')}
                </h1>
                <p className="text-slate-600 mt-1 text-[14px] sm:text-[15px]">
                  {loading
                    ? t('favourites.loading')
                    : jobs.length === 0
                      ? t('favourites.noneYet')
                      : t('favourites.countLine', {
                          count: jobs.length,
                          jobsWord: t(`favourites.jobSaved_${jobs.length === 1 ? 'one' : 'other'}`),
                          active: counts.active,
                          activeWord: t(`favourites.active${counts.active === 1 ? 'One' : 'Other'}`)
                        })}
                </p>
              </div>
              {jobs.length > 0 && (
                <Button onClick={() => navigate('/jobs/search')} className="bg-slate-900 hover:bg-slate-800" data-testid="search-jobs-btn">
                  <Search className="h-4 w-4 mr-2" />{t('favourites.searchMore')}
                </Button>
              )}
            </div>
          </header>

          {jobs.length > 0 && (
            <Card className="p-2 mb-5 border-slate-200" data-testid="favourites-filter-bar">
              <div className="flex items-center gap-1 overflow-x-auto scrollbar-thin">
                {[
                  { key: 'all', count: counts.all },
                  { key: 'active', count: counts.active },
                  { key: 'inactive', count: counts.inactive }
                ].map(f => {
                  const active = filter === f.key;
                  return (
                    <button
                      key={f.key}
                      onClick={() => setFilter(f.key)}
                      className={`flex-shrink-0 px-3 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${
                        active ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-100'
                      }`}
                      data-testid={`favourites-filter-${f.key}`}
                    >
                      <span>{t(`favourites.filters.${f.key}`)}</span>
                      <span className={`inline-flex items-center justify-center text-xs px-1.5 min-w-[20px] h-5 rounded-full font-semibold ${
                        active ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-700'
                      }`}>{f.count}</span>
                    </button>
                  );
                })}
              </div>
            </Card>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-rose-500" />
            </div>
          ) : jobs.length === 0 ? (
            <EmptyState t={t} onSearch={() => navigate('/jobs/search')} onProfile={() => navigate('/candidate/profile')} />
          ) : filtered.length === 0 ? (
            <Card className="p-10 text-center border-dashed border-slate-300">
              <Heart className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-800 font-semibold">{t('favourites.noneInFilter')}</p>
              <p className="text-slate-500 text-sm mt-1">{t('favourites.tryAnotherFilter')}</p>
            </Card>
          ) : (
            <ul className="space-y-3" data-testid="favourites-list">
              {filtered.map(j => (
                <FavouriteCard
                  key={j.id}
                  job={j}
                  t={t}
                  applied={appliedIds.has(j.id)}
                  formatSalary={formatSalary}
                  onRemove={() => remove(j.id)}
                  onView={() => navigate(`/jobs/${j.id}`)}
                />
              ))}
            </ul>
          )}

          {!loading && jobs.length > 0 && (
            <section className="mt-10" aria-labelledby="next-helpers">
              <h2 id="next-helpers" className="sr-only">{t('common.next')}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <Link to="/my-applications" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-applications">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center"><FileText className="h-4 w-4 text-blue-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('favourites.helperApplicationsTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('favourites.helperApplicationsBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
                <Link to="/jobs/search" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-search">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center"><Search className="h-4 w-4 text-emerald-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('favourites.helperSearchTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('favourites.helperSearchBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
                <Link to="/candidate/profile" className="rounded-xl border border-slate-200 bg-white p-4 hover:border-slate-900 hover:shadow-sm transition group" data-testid="helper-profile">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center"><User className="h-4 w-4 text-amber-600" /></div>
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900 text-[14px] leading-tight">{t('favourites.helperProfileTitle')}</p>
                      <p className="text-[12.5px] text-slate-500 leading-snug mt-0.5">{t('favourites.helperProfileBody')}</p>
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-300 ml-auto group-hover:text-slate-900 transition" />
                  </div>
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>
    </>
  );
}

function FavouriteCard({ job, t, applied, formatSalary, onRemove, onView }) {
  const meta = STATUS_STYLE[job.status] || STATUS_STYLE.active;
  const isActive = job.status === 'active';
  const emp = t(`favourites.employment.${(job.employment_type || "").replace(/_/g, "-")}`, { defaultValue: job.employment_type });
  const salary = formatSalary(job);

  return (
    <li className="contents">
      <Card
        className={`relative overflow-hidden border-slate-200 hover:border-slate-300 hover:shadow-md transition-all ${!isActive ? 'opacity-80' : ''}`}
        data-testid={`favourite-card-${job.id}`}
      >
        <div className={`absolute left-0 top-0 bottom-0 w-1 ${meta.stripe}`} aria-hidden="true" />
        <div className="p-5 sm:p-6 pl-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-1.5">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border ${meta.pill}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" aria-hidden="true"></span>
                  {t(`favourites.status.${job.status}.label`, { defaultValue: t('favourites.status.active.label') })}
                </span>
                {job.is_featured && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-amber-200 bg-amber-50 text-amber-800">
                    <Star className="h-3 w-3 fill-amber-500 text-amber-500" aria-hidden="true" />{t('favourites.featured')}
                  </span>
                )}
                {applied && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold border border-blue-200 bg-blue-50 text-blue-800" data-testid={`applied-badge-${job.id}`}>
                    <CheckCircle2 className="h-3 w-3" aria-hidden="true" />{t('favourites.alreadyApplied')}
                  </span>
                )}
              </div>

              <Link to={`/jobs/${job.id}`} className="inline-block group">
                <h2 className="text-[17px] sm:text-[18px] font-bold text-slate-900 leading-snug truncate group-hover:text-blue-700 transition-colors">
                  {job.title_pt}
                </h2>
              </Link>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px] text-slate-600 mt-1">
                {job.company_name && <span className="font-medium text-slate-800 truncate">{job.company_name}</span>}
                {job.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{job.location}
                  </span>
                )}
                {emp && (
                  <span className="inline-flex items-center gap-1">
                    <Briefcase className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />{emp}
                  </span>
                )}
                {job.category && (
                  <span className="text-slate-500">· {job.category}</span>
                )}
              </div>

              {salary && (
                <p className="mt-2 text-[13.5px] font-semibold text-emerald-700 inline-flex items-center gap-1.5">
                  <Banknote className="h-3.5 w-3.5" aria-hidden="true" />{salary}
                </p>
              )}

              {!isActive && (
                <p className="mt-3 text-[12.5px] text-slate-600 bg-slate-50 border border-slate-100 rounded-md px-3 py-2 inline-flex items-start gap-2 max-w-md">
                  <AlertTriangle className="h-3.5 w-3.5 text-slate-400 flex-none mt-0.5" aria-hidden="true" />
                  <span>{t(`favourites.status.${job.status}.micro`, { defaultValue: '' })}</span>
                </p>
              )}
            </div>

            <div className="flex flex-row lg:flex-col gap-2 lg:min-w-[180px] flex-none">
              {isActive ? (
                applied ? (
                  <Button onClick={onView} variant="outline" className="border-slate-300 lg:w-full" data-testid={`favourite-view-${job.id}`}>
                    {t('favourites.viewJob')}<ArrowRight className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
                  </Button>
                ) : (
                  <Button onClick={onView} className="bg-slate-900 hover:bg-slate-800 lg:w-full" data-testid={`favourite-apply-${job.id}`}>
                    <Send className="h-4 w-4 mr-2" aria-hidden="true" />{t('favourites.applyNow')}
                  </Button>
                )
              ) : (
                <Button onClick={onView} variant="outline" className="border-slate-300 lg:w-full" data-testid={`favourite-view-${job.id}`}>
                  {t('favourites.viewJob')}<ArrowRight className="h-3.5 w-3.5 ml-1.5" aria-hidden="true" />
                </Button>
              )}
              <Button
                onClick={onRemove}
                variant="outline"
                className="border-slate-200 text-slate-500 hover:text-rose-600 hover:bg-rose-50 hover:border-rose-200 lg:w-full"
                data-testid={`favourite-remove-${job.id}`}
              >
                <Trash2 className="h-4 w-4 mr-1.5" aria-hidden="true" />{t('favourites.remove')}
              </Button>
            </div>
          </div>

          {job.created_at && (
            <div className="mt-4 pt-3 border-t border-slate-100 text-[12px] text-slate-500 inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
              {t('favourites.publishedOn', { date: new Date(job.created_at).toLocaleDateString() })}
            </div>
          )}
        </div>
      </Card>
    </li>
  );
}

function EmptyState({ t, onSearch, onProfile }) {
  return (
    <Card className="p-8 sm:p-10 border-slate-200 text-center" data-testid="favourites-empty">
      <div className="w-14 h-14 rounded-full bg-rose-50 flex items-center justify-center mx-auto mb-4">
        <Heart className="h-7 w-7 fill-rose-500 text-rose-500" aria-hidden="true" />
      </div>
      <h2 className="text-[20px] sm:text-[22px] font-bold text-slate-900 mb-2">{t('favourites.emptyTitle')}</h2>
      <p className="text-slate-600 text-[14px] sm:text-[15px] max-w-md mx-auto mb-6 leading-relaxed">
        {t('favourites.emptyBody')}
      </p>
      <div className="flex flex-col sm:flex-row gap-2 justify-center max-w-md mx-auto">
        <Button onClick={onSearch} className="bg-slate-900 hover:bg-slate-800 flex-1" data-testid="empty-search-btn">
          <Search className="h-4 w-4 mr-2" />{t('favourites.emptySearch')}
        </Button>
        <Button variant="outline" onClick={onProfile} className="border-slate-300 flex-1" data-testid="empty-profile-btn">
          <User className="h-4 w-4 mr-2" />{t('favourites.emptyProfile')}
        </Button>
      </div>

      <div className="mt-8 pt-6 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px] text-slate-600">
        <div className="flex items-center justify-center gap-2"><Heart className="h-4 w-4 text-rose-500" />{t('favourites.trustFree')}</div>
        <div className="flex items-center justify-center gap-2"><Calendar className="h-4 w-4 text-blue-600" />{t('favourites.trustReturn')}</div>
        <div className="flex items-center justify-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" />{t('favourites.trustRealtime')}</div>
      </div>
    </Card>
  );
}
